import { formatPhoneNumberIntl } from 'react-phone-number-input'

export const usd = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 0,
  maximumFractionDigits: 0,
})

export const decimalUsd = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
})

export const decimalOnly = new Intl.NumberFormat('en-US', {
  style: 'decimal',
  currency: 'USD',
  minimumFractionDigits: 0,
  maximumFractionDigits: 2,
})

export const formatUsd = (num?: number): string => {
  if (num === null || num === undefined) {
    return ''
  }
  return usd.format(num)
}

export const formatUsdDecimal = (num: number): string => {
  if (num === null || num === undefined) {
    return ''
  }
  return decimalUsd.format(num)
}

export const formatDecimalOnly = (num?: number): string => {
  if (num === null || num === undefined) {
    return ''
  }
  return decimalOnly.format(num)
}

export const twoDecimalNum = new Intl.NumberFormat('en-US', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
})

export const integerNum = new Intl.NumberFormat('en-US', {
  minimumFractionDigits: 0,
  maximumFractionDigits: 0,
})

export const percentageNum = new Intl.NumberFormat('en-US', {
  minimumFractionDigits: 1,
  maximumFractionDigits: 1,
})

export const formatPercentage = (num: number): string => {
  if (num === null || num === undefined) {
    return ''
  }
  return percentageNum.format(num)
}

export const formatInteger = (num?: number): string => {
  if (num === null || num === undefined) {
    return ''
  }

  return integerNum.format(num)
}

export const formatTwoDecimal = (num: number): string => {
  if (num === null || num === undefined) {
    return ''
  }
  return twoDecimalNum.format(num)
}

export const formatPhoneNumber = ({
  phoneNumber,
  extension,
}: {
  phoneNumber: string
  extension?: string
}): string => {
  const phoneNumberWithPlus = phoneNumber.startsWith('+')
    ? phoneNumber
    : `+${phoneNumber}`
  let formattedPhoneNumber =
    formatPhoneNumberIntl(phoneNumberWithPlus) || phoneNumberWithPlus
  // This will return +1 213 373 4253
  // I want +1 (213) 373-4253
  const [countryCode, areaCode, firstThree, lastFour] =
    formattedPhoneNumber.split(' ')
  if (countryCode && areaCode && firstThree && lastFour) {
    formattedPhoneNumber = `${countryCode} (${areaCode}) ${firstThree}-${lastFour}`
  }
  return extension
    ? `${formattedPhoneNumber}, ext. ${extension}`
    : formattedPhoneNumber
}

/**
 * Generic helper to format a value or return a fallback for nullish values.
 * Unlike truthy checks, this correctly handles 0 as a valid value.
 */
export const formatOrFallback = <T>(
  value: T | null | undefined,
  formatter: (val: T) => string,
  fallback: string = '-'
): string =>
  value !== null && value !== undefined ? formatter(value) : fallback

export const integerFormatterOrFallback = (
  value: number | null | undefined,
  fallback: string = '-'
) => formatOrFallback(value, formatInteger, fallback)

export const percentFormatterOrFallback = (
  value: number | null | undefined,
  fallback: string = '-'
) => formatOrFallback(value, (v) => formatInteger(v) + '%', fallback)

export const usdFormatterOrFallbackString = (
  value: string | null | undefined,
  fallback: string = '-'
) => formatOrFallback(value, (v) => formatUsd(parseInt(v)), fallback)

export const usdFormatterOrFallback = (
  value: number | null | undefined,
  fallback: string = '-'
) => formatOrFallback(value, formatUsd, fallback)

// formatDateWithTimeAndMinutes formats a date into "MM/DD/YYYY HH:MM AM/PM" format
export const formatDateWithTimeAndMinutes = (date: Date | string) => {
  if (!(date instanceof Date)) {
    date = new Date(date)
  }

  const options: Intl.DateTimeFormatOptions = {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  }
  return new Intl.DateTimeFormat('en-US', options).format(date)
}

// formatDateWithTimeAndMinutes2 formats a date into separate date and time strings
export const formatDateWithTimeAndMinutes2 = (date: Date | string) => {
  if (!(date instanceof Date)) {
    date = new Date(date)
  }

  const dateOptions: Intl.DateTimeFormatOptions = {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }

  const timeOptions: Intl.DateTimeFormatOptions = {
    hour: '2-digit',
    minute: '2-digit',
  }

  const formattedDate = new Intl.DateTimeFormat('en-US', dateOptions).format(
    date
  )
  const formattedTime = new Intl.DateTimeFormat('en-US', timeOptions).format(
    date
  )

  return {
    date: formattedDate,
    time: formattedTime,
  }
}

// formatDate formats a date string into "MMM DD, YYYY" format (e.g., "Jan 01, 2023")
export function formatDate(dateString?: string | null) {
  if (!dateString) return '-'
  const date = new Date(dateString)
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  })
}

// formatDate2 formats a date string into "MM/DD/YYYY" format (e.g., "07/31/2025")
export function formatDate2(dateString?: string | null) {
  if (!dateString) return '-'
  const date = new Date(dateString)
  return date.toLocaleDateString('en-US', {
    month: '2-digit',
    day: '2-digit',
    year: 'numeric',
  })
}

// Additional formatting functions for demographics and charts
export function formatCurrency(value: number): string {
  if (value >= 1000000000) {
    return `$${(value / 1000000000).toFixed(1)}B`
  }
  if (value >= 1000000) {
    return `$${(value / 1000000).toFixed(1)}M`
  }
  if (value >= 1000) {
    return `$${(value / 1000).toFixed(1)}K`
  }
  return `$${value.toLocaleString()}`
}

export function formatPercentageValue(value: number): string {
  return `${(value * 100).toFixed(1)}%`
}

export function formatPopulationDensity(value: number): string {
  return `${Math.round(value)} ppl / mile²`
}

export function formatVolume(value: number, unit: string): string {
  return `${value.toLocaleString()} ${unit}`
}

/**
 * Format an address from its component parts.
 * Filters out null/undefined values and joins with commas.
 */
export function formatAddress(parts: {
  street?: string | null
  city?: string | null
  state?: string | null
  zip?: string | null
}): string | null {
  const addressParts = [
    parts.street,
    parts.city,
    parts.state,
    parts.zip,
  ].filter(Boolean)
  return addressParts.length > 0 ? addressParts.join(', ') : null
}

/**
 * Capitalize the first letter of a string.
 *
 * @param str - The string to capitalize
 * @returns The string with its first letter capitalized
 *
 * @example
 * capitalizeFirst('select products') // => 'Select products'
 * capitalizeFirst('Company') // => 'Company'
 */
export function capitalizeFirst(str: string): string {
  if (!str) return str
  return str.charAt(0).toUpperCase() + str.slice(1)
}
